import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Eye, Rocket } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { DateCell } from "@/components/admin/DateCell";
import { adminGetUser } from "@/lib/admin.functions";
import { PLANS, type PlanId } from "@/lib/plans";
import { categoryLabel } from "@/lib/categories";
import { setAdminPreview, useAdminPreview } from "@/lib/admin-preview";

export const Route = createFileRoute("/_authenticated/admin/visa-som/$userId")({
  head: () => ({
    meta: [
      { title: "Visa som användare — Administration | Sellavo" },
      {
        name: "description",
        content: "Se en användares annonser och kontonivå precis som användaren ser dem.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Visa som användare">
      <Body />
    </AdminShell>
  ),
});

function planName(plan: string) {
  return PLANS[(plan as PlanId) in PLANS ? (plan as PlanId) : "free"].name;
}

function Body() {
  const { userId } = Route.useParams();
  const queryClient = useQueryClient();
  const preview = useAdminPreview();
  const fetchUser = useServerFn(adminGetUser);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-user", userId],
    queryFn: async () => {
      const res = await fetchUser({ data: { userId } });
      if (!res) throw new Error("Kunde inte hämta användaren.");
      return res;
    },
  });

  if (isLoading) return <p className="text-muted-foreground">Laddar…</p>;
  if (error instanceof Error || !data)
    return (
      <p className="text-destructive">
        {error instanceof Error ? error.message : "Kunde inte hämta användaren."}
      </p>
    );

  const u = data as any;
  const label = (u.displayName || u.email || "Användare") as string;
  const ads = (u.ads ?? []) as Array<{
    slug: string;
    title: string;
    price: number;
    category: string;
    location: string | null;
    created_at: string;
    is_paused: boolean;
    promoted_until: string | null;
  }>;
  const now = Date.now();

  const startPreview = () => {
    setAdminPreview({ plan: u.plan as PlanId, user: { id: userId, label } });
    void queryClient.invalidateQueries();
  };

  return (
    <div className="space-y-6">
      <Link
        to="/admin/anvandare/$userId"
        params={{ userId }}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Tillbaka till användarprofilen
      </Link>

      <section className="rounded-md border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground">{label}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {u.email} · {planName(u.plan)} · {ads.length} annonser
            </p>
          </div>
          <Button
            variant={preview.user?.id === userId ? "secondary" : "default"}
            onClick={startPreview}
            className="gap-1.5"
          >
            <Eye className="h-4 w-4" />
            {preview.user?.id === userId
              ? "Förhandsvisning pågår"
              : "Visa sajten som den här användaren"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Allt här är skrivskyddat. Du kan se vad användaren har, men inget skickas eller ändras i
          användarens namn.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-heading text-lg font-bold text-foreground">Användarens annonser</h3>
        {ads.length === 0 ? (
          <p className="text-sm text-muted-foreground">Användaren har inga annonser.</p>
        ) : (
          <ul className="space-y-2">
            {ads.map((ad) => {
              const promoted = !!ad.promoted_until && new Date(ad.promoted_until).getTime() > now;
              return (
                <li
                  key={ad.slug}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3"
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/a/$slug"
                      params={{ slug: ad.slug }}
                      className="block truncate font-heading font-semibold text-foreground hover:text-primary"
                    >
                      {ad.title}
                    </Link>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {ad.price.toLocaleString("sv-SE")} kr · {categoryLabel(ad.category)}
                      {ad.location ? ` · ${ad.location}` : ""}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Skapad <DateCell value={ad.created_at} />
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {promoted && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                        <Rocket className="h-3 w-3" /> Framlyft
                      </span>
                    )}
                    {ad.is_paused && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                        Pausad
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
